import { IParseCreatorsGoogleSheetRequest } from "@shared/lib/types/brief";
import {
  IBriefDeliverableResponse,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";
import { IDeliverableConfigurator } from "@shared/lib/types/deliverableConfigurator";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { useMemo } from "react";
import { isProcessedCreatorRow } from "../../../lib/constants";
import styles from "../process.module.scss";
import { ErrorList } from "./ErrorList";
import { ProcessedTable } from "./ProcessedTable";

export type ReviewTab = "processed" | "error";

interface ProcessStepProps {
  rows: IParseCreatorsGoogleSheetRequest[];
  deliverables: IBriefDeliverableResponse[];
  configurators: IDeliverableConfigurator[];
  customFields: IFieldConfig[];
  activeTab: ReviewTab;
  onTabChange: (tab: ReviewTab) => void;
}

export function ProcessStep({
  rows,
  deliverables,
  configurators,
  customFields,
  activeTab,
  onTabChange,
}: ProcessStepProps) {
  const processedRows = useMemo(
    () =>
      rows.filter(
        (row) => isProcessedCreatorRow(row) && row.socialMediaObjectId
      ),
    [rows]
  );
  const errorRows = useMemo(
    () =>
      rows.filter(
        (row) => !isProcessedCreatorRow(row) || !row.socialMediaObjectId
      ),
    [rows]
  );

  const withCommercials = processedRows.filter(
    (row) => row.brandCost != null || row.influencerCost != null
  ).length;
  const withDeliverables = processedRows.filter((row) =>
    Boolean(row.deliverablesV2Id)
  ).length;

  return (
    <div className={styles.processBody}>
      <div className={styles.tabCards}>
        <button
          type="button"
          onClick={() => onTabChange("processed")}
          className={`${styles.tabCard} ${
            activeTab === "processed" ? styles.tabCardProcessedActive : ""
          }`}
        >
          <div className={`${styles.tabCardIcon} ${styles.tabCardIconSuccess}`}>
            <CheckCircle2 size={18} />
          </div>
          <div>
            <div className={styles.tabCardCountSuccess}>
              {processedRows.length}
            </div>
            <div className={styles.tabCardLabelSuccess}>Ready to add</div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => onTabChange("error")}
          className={`${styles.tabCard} ${
            activeTab === "error" ? styles.tabCardErrorActive : ""
          }`}
        >
          <div className={`${styles.tabCardIcon} ${styles.tabCardIconError}`}>
            <AlertCircle size={18} />
          </div>
          <div>
            <div className={styles.tabCardCountError}>{errorRows.length}</div>
            <div className={styles.tabCardLabelError}>Couldn’t import</div>
          </div>
        </button>
      </div>

      <p className={styles.processSummary}>
        {processedRows.length > 0 ? (
          <>
            <span className={styles.processSummaryStrong}>
              {processedRows.length} creator
              {processedRows.length !== 1 ? "s" : ""}
            </span>
            {withCommercials > 0 && <> · {withCommercials} with commercials</>}
            {withDeliverables > 0 && (
              <> · {withDeliverables} with deliverables</>
            )}
          </>
        ) : (
          "No creators passed validation. Check who failed and why below."
        )}
      </p>

      <div className={styles.processHeading}>
        <h4>
          {activeTab === "processed"
            ? "Creators going into the brief"
            : "Why these creators failed"}
        </h4>
        {activeTab === "processed" && processedRows.length > 0 && (
          <span>Review commercials and deliverables before confirming</span>
        )}
      </div>

      <div className={styles.tableWrap}>
        {activeTab === "processed" ? (
          <ProcessedTable
            rows={processedRows}
            customFields={customFields}
            deliverables={deliverables}
            configurators={configurators}
            onViewErrors={() => onTabChange("error")}
          />
        ) : (
          <ErrorList rows={errorRows} />
        )}
      </div>
    </div>
  );
}
